import { StyleSheet, Text, View } from 'react-native'
import { TRANSITION_TO_SPLIT_SCREEN_WIDTH } from '@/src/experiences/rewards/types'
import { useMediaQuery } from 'react-responsive'
import { DesignSystem } from '@/src/design-system'
import { mesaColorNames, typography } from '@/src/common/theme'

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  splitRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  caption: {
    ...typography['label/sm-bold'],
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
  },
  value: {
    ...typography['heading/heading1'],
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
  },
  valuePending: {
    color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
  },
  valueSkeleton: {
    height: 28,
    width: 100,
    marginVertical: 5.5,
    opacity: 0.5,
  },
  smallValueSkeleton: {
    height: 14,
    width: 100,
    opacity: 0.5,
  },
  metric: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
})

type BalanceHeaderProps = {
  currentPoints: number | undefined
  lifetimeEarnedPoints: number | undefined
  lifetimeRedeemedPoints: number | undefined
  view: 'compact' | 'full'
}

export function RewardsComponentsBalanceHeader({
  currentPoints,
  lifetimeEarnedPoints,
  lifetimeRedeemedPoints,
  view,
}: BalanceHeaderProps) {
  const hasCurrentPoints = typeof currentPoints === 'number'
  const hasLifetimeEarnedPoints = typeof lifetimeEarnedPoints === 'number'
  const hasLifetimeRedeemedPoints = typeof lifetimeRedeemedPoints === 'number'
  const isSplitScreen = useMediaQuery({ minWidth: TRANSITION_TO_SPLIT_SCREEN_WIDTH })
  const isSplitScreenCompact = isSplitScreen && view === 'compact'

  return (
    <View style={styles.container}>
      <View style={[styles.splitRow, isSplitScreenCompact && { justifyContent: 'space-around' }]}>
        <View style={[styles.metric, !isSplitScreenCompact && { alignItems: 'flex-start' }]}>
          <Text style={styles.caption}>Available Mesa Points</Text>
          {hasCurrentPoints ? (
            <Text style={styles.value}>{currentPoints.toLocaleString()}</Text>
          ) : (
            <DesignSystem.Skeleton.Skeleton
              style={styles.valueSkeleton}
              skeletonProps={{
                width: styles.valueSkeleton.width,
                height: styles.valueSkeleton.height,
              }}
            />
          )}
        </View>
      </View>
      {view === 'full' && (
        <>
          <View style={styles.splitRow}>
            <Text style={styles.caption}>Lifetime Mesa Points Earned</Text>
            {hasLifetimeEarnedPoints ? (
              <Text style={styles.caption}>{lifetimeEarnedPoints.toLocaleString()}</Text>
            ) : (
              <DesignSystem.Skeleton.Skeleton style={styles.smallValueSkeleton} />
            )}
          </View>
          <View style={styles.splitRow}>
            <Text style={styles.caption}>Mesa Points Redeemed</Text>
            {hasLifetimeRedeemedPoints ? (
              <Text style={styles.caption}>{lifetimeRedeemedPoints.toLocaleString()}</Text>
            ) : (
              <DesignSystem.Skeleton.Skeleton style={styles.smallValueSkeleton} />
            )}
          </View>
        </>
      )}
    </View>
  )
}
